import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../environment/environment';
import { ComponentDataDestService } from '../../../services/component-data-dest/component-data-dest.service';

export interface ModuleItem {
  module_id: number;
  name: string;
  visibility?: string;
  versions?: ModuleVersionItem[];
}

export interface ModuleVersionItem {
  version_id: number;
  module_id: number;
  valid_from: string;
  valid_to: string | null;
  maintenance_minutes: number;
  descriptions?: Record<
    string,
    { language_code: string; translation_text: string; description_id?: number }
  >;
  description?: string;
  dateRange?: string;
}

export interface SubtypeItem {
  subtype_id: number;
  name: string;
  visibility?: string;
  versions?: SubtypeVersionItem[];
}

export interface SubtypeVersionItem {
  version_id: number;
  subtype_id: number;
  valid_from: string;
  valid_to: string | null;
  descriptions?: Record<
    string,
    { language_code: string; translation_text: string }
  >;
  description?: string;
  dateRange?: string;
}

export interface EntityListGroup<T> {
  letter: string;
  items: T[];
}

@Component({
  selector: 'app-duna-modules-subtypes',
  standalone: true,
  imports: [
    FormsModule,
    MatTabsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatIconModule,
  ],
  templateUrl: './duna-modules-subtypes.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-modules-subtypes.component.scss',
})
export class DunaModulesSubtypesComponent implements OnInit, OnDestroy {
  modules: ModuleItem[] = [];
  subtypes: SubtypeItem[] = [];
  moduleSearch = '';
  subtypeSearch = '';
  moduleLetterFilter: string | null = null;
  subtypeLetterFilter: string | null = null;
  moduleSortAsc = true;
  subtypeSortAsc = true;

  private readonly letterIndexOrder = [
    '0-9',
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
    'Z',
    'Æ',
    'Ø',
    'Å',
    '#',
  ];
  selectedModule: ModuleItem | null = null;
  selectedSubtype: SubtypeItem | null = null;
  moduleVersions: ModuleVersionItem[] = [];
  subtypeVersions: SubtypeVersionItem[] = [];
  loadingModules = false;
  loadingSubtypes = false;
  loadingModuleVersions = false;
  loadingSubtypeVersions = false;
  saving = false;
  errorMessage: string | null = null;

  showAddModuleVersion = false;
  showAddSubtypeVersion = false;
  editingModuleVersionId: number | null = null;
  editingSubtypeVersionId: number | null = null;
  editingModuleName = false;
  editingSubtypeName = false;
  editModuleNameForm = { name: '' };
  editSubtypeNameForm = { name: '' };

  showAddModule = false;
  showAddSubtype = false;
  addModuleForm = { name: '', visibility: 'private' as string };
  addSubtypeForm = { name: '', visibility: 'private' as string };

  addModuleVersionForm = {
    valid_from: '',
    valid_to: '',
    maintenance_minutes: 0,
    description_da: '',
    description_en: '',
    description_de: '',
  };
  addSubtypeVersionForm = { valid_from: '', valid_to: '', description_da: '' };
  editModuleVersionForm = {
    valid_from: '',
    valid_to: '',
    maintenance_minutes: 0,
    description_da: '',
    description_en: '',
    description_de: '',
  };
  editSubtypeVersionForm = { valid_from: '', valid_to: '', description_da: '' };

  private destroy$ = new Subject<void>();
  private pendingModuleId: number | null = null;
  private pendingSubtypeId: number | null = null;

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private dataDestService: ComponentDataDestService
  ) {}

  ngOnInit(): void {
    this.loadModules();
    this.loadSubtypes();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cachedFilteredModules: ModuleItem[] = [];
  private cachedFilteredModulesForList: ModuleItem[] = [];
  private cachedGroupedModules: EntityListGroup<ModuleItem>[] = [];
  private cachedAvailableModuleLetters: string[] = [];
  private cachedModuleLetterIndex: string[] = [];
  private lastModuleListKey = '';

  private cachedFilteredSubtypes: SubtypeItem[] = [];
  private cachedFilteredSubtypesForList: SubtypeItem[] = [];
  private cachedGroupedSubtypes: EntityListGroup<SubtypeItem>[] = [];
  private cachedAvailableSubtypeLetters: string[] = [];
  private cachedSubtypeLetterIndex: string[] = [];
  private lastSubtypeListKey = '';

  private readonly emptyDescriptionLines: Array<{
    lang: string;
    label: string;
    text: string;
  }> = [];
  private readonly descriptionLinesCache = new WeakMap<
    object,
    Array<{ lang: string; label: string; text: string }>
  >();

  get filteredModules(): ModuleItem[] {
    this.ensureModuleListCache();
    return this.cachedFilteredModules;
  }

  get filteredSubtypes(): SubtypeItem[] {
    this.ensureSubtypeListCache();
    return this.cachedFilteredSubtypes;
  }

  get filteredModulesForList(): ModuleItem[] {
    this.ensureModuleListCache();
    return this.cachedFilteredModulesForList;
  }

  get filteredSubtypesForList(): SubtypeItem[] {
    this.ensureSubtypeListCache();
    return this.cachedFilteredSubtypesForList;
  }

  get groupedModules(): EntityListGroup<ModuleItem>[] {
    this.ensureModuleListCache();
    return this.cachedGroupedModules;
  }

  get groupedSubtypes(): EntityListGroup<SubtypeItem>[] {
    this.ensureSubtypeListCache();
    return this.cachedGroupedSubtypes;
  }

  get availableModuleLetters(): string[] {
    this.ensureModuleListCache();
    return this.cachedAvailableModuleLetters;
  }

  get availableSubtypeLetters(): string[] {
    this.ensureSubtypeListCache();
    return this.cachedAvailableSubtypeLetters;
  }

  get moduleLetterIndex(): string[] {
    this.ensureModuleListCache();
    return this.cachedModuleLetterIndex;
  }

  get subtypeLetterIndex(): string[] {
    this.ensureSubtypeListCache();
    return this.cachedSubtypeLetterIndex;
  }

  private moduleListKey(): string {
    return `${this.moduleSearch}\0${this.moduleLetterFilter}\0${this.moduleSortAsc}\0${this.modules
      .map((m) => `${m.module_id}:${m.name}`)
      .join('\0')}`;
  }

  private subtypeListKey(): string {
    return `${this.subtypeSearch}\0${this.subtypeLetterFilter}\0${this.subtypeSortAsc}\0${this.subtypes
      .map((s) => `${s.subtype_id}:${s.name}`)
      .join('\0')}`;
  }

  private ensureModuleListCache(): void {
    const key = this.moduleListKey();
    if (key === this.lastModuleListKey) {
      return;
    }
    this.lastModuleListKey = key;
    const q = this.moduleSearch.trim().toLowerCase();
    this.cachedFilteredModules = q
      ? this.modules.filter((m) => (m.name || '').toLowerCase().includes(q))
      : this.modules;
    this.cachedFilteredModulesForList = this.applyListFilters(
      this.cachedFilteredModules,
      this.moduleLetterFilter,
      this.moduleSortAsc
    );
    this.cachedGroupedModules = this.groupByFirstLetter(
      this.cachedFilteredModulesForList
    );
    this.cachedAvailableModuleLetters = this.getAvailableLetters(
      this.cachedFilteredModules
    );
    this.cachedModuleLetterIndex = this.buildLetterIndex(
      this.cachedAvailableModuleLetters
    );
  }

  private ensureSubtypeListCache(): void {
    const key = this.subtypeListKey();
    if (key === this.lastSubtypeListKey) {
      return;
    }
    this.lastSubtypeListKey = key;
    const q = this.subtypeSearch.trim().toLowerCase();
    this.cachedFilteredSubtypes = q
      ? this.subtypes.filter((s) => (s.name || '').toLowerCase().includes(q))
      : this.subtypes;
    this.cachedFilteredSubtypesForList = this.applyListFilters(
      this.cachedFilteredSubtypes,
      this.subtypeLetterFilter,
      this.subtypeSortAsc
    );
    this.cachedGroupedSubtypes = this.groupByFirstLetter(
      this.cachedFilteredSubtypesForList
    );
    this.cachedAvailableSubtypeLetters = this.getAvailableLetters(
      this.cachedFilteredSubtypes
    );
    this.cachedSubtypeLetterIndex = this.buildLetterIndex(
      this.cachedAvailableSubtypeLetters
    );
  }

  clearModuleSearch(): void {
    this.moduleSearch = '';
    this.moduleLetterFilter = null;
    this.cdr.markForCheck();
  }

  clearSubtypeSearch(): void {
    this.subtypeSearch = '';
    this.subtypeLetterFilter = null;
    this.cdr.markForCheck();
  }

  onModuleSearchChange(): void {
    this.moduleLetterFilter = null;
  }

  onSubtypeSearchChange(): void {
    this.subtypeLetterFilter = null;
  }

  toggleModuleSort(): void {
    this.moduleSortAsc = !this.moduleSortAsc;
    this.cdr.markForCheck();
  }

  toggleSubtypeSort(): void {
    this.subtypeSortAsc = !this.subtypeSortAsc;
    this.cdr.markForCheck();
  }

  toggleModuleLetterFilter(letter: string): void {
    if (!this.availableModuleLetters.includes(letter)) return;
    this.moduleLetterFilter =
      this.moduleLetterFilter === letter ? null : letter;
    this.cdr.markForCheck();
  }

  toggleSubtypeLetterFilter(letter: string): void {
    if (!this.availableSubtypeLetters.includes(letter)) return;
    this.subtypeLetterFilter =
      this.subtypeLetterFilter === letter ? null : letter;
    this.cdr.markForCheck();
  }

  private applyListFilters<T>(
    items: T[],
    letterFilter: string | null,
    sortAsc: boolean
  ): T[] {
    let result = items;
    if (letterFilter) {
      result = result.filter(
        (item) =>
          this.getEntityFirstLetter((item as { name?: string }).name || '') ===
          letterFilter
      );
    }
    return [...result].sort((a, b) => {
      const cmp = ((a as { name?: string }).name || '').localeCompare(
        (b as { name?: string }).name || '',
        'da',
        { sensitivity: 'base', numeric: true }
      );
      return sortAsc ? cmp : -cmp;
    });
  }

  private groupByFirstLetter<T extends { name?: string }>(
    items: T[]
  ): EntityListGroup<T>[] {
    const groups = new Map<string, T[]>();
    for (const item of items) {
      const letter = this.getEntityFirstLetter(item.name || '');
      if (!groups.has(letter)) groups.set(letter, []);
      groups.get(letter)!.push(item);
    }
    return Array.from(groups.entries())
      .sort(([a], [b]) => this.compareLetters(a, b))
      .map(([letter, groupItems]) => ({ letter, items: groupItems }));
  }

  private getAvailableLetters<T extends { name?: string }>(
    items: T[]
  ): string[] {
    const letters = new Set<string>();
    for (const item of items) {
      letters.add(this.getEntityFirstLetter(item.name || ''));
    }
    return Array.from(letters).sort((a, b) => this.compareLetters(a, b));
  }

  private buildLetterIndex(available: string[]): string[] {
    const availableSet = new Set(available);
    return this.letterIndexOrder.filter((letter) => availableSet.has(letter));
  }

  getEntityFirstLetter(name: string): string {
    const trimmed = name.trim();
    if (!trimmed) return '#';
    const first = trimmed.charAt(0).toUpperCase();
    if (/[A-ZÆØÅ]/.test(first)) return first;
    if (/[0-9]/.test(first)) return '0-9';
    return '#';
  }

  private compareLetters(a: string, b: string): number {
    const idxA = this.letterIndexOrder.indexOf(a);
    const idxB = this.letterIndexOrder.indexOf(b);
    return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
  }

  loadModules(): void {
    this.loadingModules = true;
    this.cdr.markForCheck();
    this.http
      .get<{ items?: ModuleItem[] }>(`${environment.apiUrl}/events/modules`, {
        withCredentials: true,
        params: { pageSize: '500', pageIndex: '0' },
      })
      .pipe(catchError(() => of({ items: [] })))
      .subscribe((res) => {
        this.modules = res.items || (Array.isArray(res) ? res : []);
        this.loadingModules = false;
        if (this.pendingModuleId != null) {
          const m = this.modules.find(
            (x) => x.module_id === this.pendingModuleId
          );
          if (m) this.selectModule(m);
          this.pendingModuleId = null;
        }
        this.cdr.markForCheck();
      });
  }

  loadSubtypes(): void {
    this.loadingSubtypes = true;
    this.cdr.markForCheck();
    this.http
      .get<{ items?: SubtypeItem[] }>(`${environment.apiUrl}/events/subtypes`, {
        withCredentials: true,
        params: { pageSize: '500', pageIndex: '0' },
      })
      .pipe(catchError(() => of({ items: [] })))
      .subscribe((res) => {
        this.subtypes = res.items || (Array.isArray(res) ? res : []);
        this.loadingSubtypes = false;
        if (this.pendingSubtypeId != null) {
          const s = this.subtypes.find(
            (x) => x.subtype_id === this.pendingSubtypeId
          );
          if (s) this.selectSubtype(s);
          this.pendingSubtypeId = null;
        }
        this.cdr.markForCheck();
      });
  }

  selectModule(m: ModuleItem): void {
    this.selectedModule = m;
    this.showAddModuleVersion = false;
    this.editingModuleVersionId = null;
    this.editingModuleName = false;
    this.loadModuleVersions();
  }

  selectSubtype(s: SubtypeItem): void {
    this.selectedSubtype = s;
    this.showAddSubtypeVersion = false;
    this.editingSubtypeVersionId = null;
    this.editingSubtypeName = false;
    this.loadSubtypeVersions();
  }

  openEditModuleName(): void {
    if (!this.selectedModule) return;
    this.editModuleNameForm = { name: this.selectedModule.name || '' };
    this.editingModuleName = true;
    this.errorMessage = null;
    this.cdr.markForCheck();
  }

  cancelEditModuleName(): void {
    this.editingModuleName = false;
    this.cdr.markForCheck();
  }

  saveEditModuleName(): void {
    const name = this.editModuleNameForm.name.trim();
    if (!name || !this.selectedModule?.module_id) return;
    this.errorMessage = null;
    this.saving = true;
    this.cdr.markForCheck();
    const dest = this.dataDestService.getDataDest('update_module');
    if (!dest) {
      this.errorMessage = 'update_module dest not registered';
      this.saving = false;
      this.cdr.markForCheck();
      return;
    }
    (dest as (data: any) => any)({
      module_id: this.selectedModule.module_id,
      name,
    }).subscribe({
      next: (res: { success?: boolean; message?: string; error?: string }) => {
        if (res && res.success === false) {
          this.errorMessage =
            res.message || res.error || 'Kunne ikke opdatere modulnavn';
          this.saving = false;
          this.cdr.markForCheck();
          return;
        }
        this.selectedModule = { ...this.selectedModule!, name };
        const idx = this.modules.findIndex(
          (x) => x.module_id === this.selectedModule!.module_id
        );
        if (idx >= 0) this.modules[idx] = { ...this.modules[idx], name };
        this.editingModuleName = false;
        this.saving = false;
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.errorMessage =
          err?.error?.message ||
          err?.message ||
          'Kunne ikke opdatere modulnavn';
        this.saving = false;
        this.cdr.markForCheck();
      },
    });
  }

  openEditSubtypeName(): void {
    if (!this.selectedSubtype) return;
    this.editSubtypeNameForm = { name: this.selectedSubtype.name || '' };
    this.editingSubtypeName = true;
    this.errorMessage = null;
    this.cdr.markForCheck();
  }

  cancelEditSubtypeName(): void {
    this.editingSubtypeName = false;
    this.cdr.markForCheck();
  }

  saveEditSubtypeName(): void {
    const name = this.editSubtypeNameForm.name.trim();
    if (!name || !this.selectedSubtype?.subtype_id) return;
    this.errorMessage = null;
    this.saving = true;
    this.cdr.markForCheck();
    const dest = this.dataDestService.getDataDest('update_subtype');
    if (!dest) {
      this.errorMessage = 'update_subtype dest not registered';
      this.saving = false;
      this.cdr.markForCheck();
      return;
    }
    (dest as (data: any) => any)({
      subtype_id: this.selectedSubtype.subtype_id,
      name,
    }).subscribe({
      next: (res: { success?: boolean; message?: string; error?: string }) => {
        if (res && res.success === false) {
          this.errorMessage =
            res.message || res.error || 'Kunne ikke opdatere navn';
          this.saving = false;
          this.cdr.markForCheck();
          return;
        }
        this.selectedSubtype = { ...this.selectedSubtype!, name };
        const idx = this.subtypes.findIndex(
          (x) => x.subtype_id === this.selectedSubtype!.subtype_id
        );
        if (idx >= 0) this.subtypes[idx] = { ...this.subtypes[idx], name };
        this.editingSubtypeName = false;
        this.saving = false;
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.errorMessage =
          err?.error?.message || err?.message || 'Kunne ikke opdatere navn';
        this.saving = false;
        this.cdr.markForCheck();
      },
    });
  }

  deleteModule(): void {
    if (!this.selectedModule?.module_id) return;
    const name = this.selectedModule.name || 'modulet';
    if (
      !confirm(
        `Er du sikker på, at du vil slette "${name}"? Modulet skjules fra lister og kan kun slettes hvis det ikke er tilknyttet events eller uddannelsesveje.`
      )
    ) {
      return;
    }
    this.errorMessage = null;
    this.saving = true;
    this.cdr.markForCheck();
    const dest = this.dataDestService.getDataDest('delete_module');
    if (!dest) {
      this.errorMessage = 'delete_module dest not registered';
      this.saving = false;
      this.cdr.markForCheck();
      return;
    }
    const deletedId = this.selectedModule.module_id;
    (dest as (data: any) => any)({ module_id: deletedId }).subscribe({
      next: () => {
        this.modules = this.modules.filter((m) => m.module_id !== deletedId);
        this.selectedModule = null;
        this.moduleVersions = [];
        this.editingModuleName = false;
        this.showAddModuleVersion = false;
        this.editingModuleVersionId = null;
        this.saving = false;
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.errorMessage =
          err?.error?.message ||
          err?.message ||
          'Kunne ikke slette modulet';
        this.saving = false;
        this.cdr.markForCheck();
      },
    });
  }

  deleteSubtype(): void {
    if (!this.selectedSubtype?.subtype_id) return;
    const name = this.selectedSubtype.name || 'indholdstemaet';
    if (
      !confirm(
        `Er du sikker på, at du vil slette "${name}"? Indholdstemaet skjules fra lister og kan kun slettes hvis det ikke er tilknyttet events eller uddannelsesveje.`
      )
    ) {
      return;
    }
    this.errorMessage = null;
    this.saving = true;
    this.cdr.markForCheck();
    const dest = this.dataDestService.getDataDest('delete_subtype');
    if (!dest) {
      this.errorMessage = 'delete_subtype dest not registered';
      this.saving = false;
      this.cdr.markForCheck();
      return;
    }
    const deletedId = this.selectedSubtype.subtype_id;
    (dest as (data: any) => any)({ subtype_id: deletedId }).subscribe({
      next: () => {
        this.subtypes = this.subtypes.filter((s) => s.subtype_id !== deletedId);
        this.selectedSubtype = null;
        this.subtypeVersions = [];
        this.editingSubtypeName = false;
        this.showAddSubtypeVersion = false;
        this.editingSubtypeVersionId = null;
        this.saving = false;
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.errorMessage =
          err?.error?.message ||
          err?.message ||
          'Kunne ikke slette indholdstemaet';
        this.saving = false;
        this.cdr.markForCheck();
      },
    });
  }

  openAddModule(): void {
    this.showAddModule = true;
    this.addModuleForm = { name: '', visibility: 'private' };
    this.errorMessage = null;
    this.cdr.markForCheck();
  }

  cancelAddModule(): void {
    this.showAddModule = false;
    this.errorMessage = null;
    this.cdr.markForCheck();
  }

  saveAddModule(): void {
    const name = this.addModuleForm.name?.trim();
    if (!name) return;
    this.errorMessage = null;
    this.saving = true;
    this.cdr.markForCheck();
    const payload = {
      module: {
        name,
        visibility: this.addModuleForm.visibility?.trim() || 'private',
      },
    };
    this.http
      .post<{ data?: { module_id: number }; module_id?: number }>(
        `${environment.apiUrl}/events/modules`,
        payload,
        { withCredentials: true }
      )
      .subscribe({
        next: (res) => {
          const moduleId = res?.data?.module_id ?? res?.module_id;
          this.showAddModule = false;
          this.saving = false;
          this.pendingModuleId = moduleId ?? null;
          this.loadModules();
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.errorMessage =
            err?.error?.message || err?.message || 'Kunne ikke oprette modul';
          this.saving = false;
          this.cdr.markForCheck();
        },
      });
  }

  openAddSubtype(): void {
    this.showAddSubtype = true;
    this.addSubtypeForm = { name: '', visibility: 'private' };
    this.errorMessage = null;
    this.cdr.markForCheck();
  }

  cancelAddSubtype(): void {
    this.showAddSubtype = false;
    this.errorMessage = null;
    this.cdr.markForCheck();
  }

  saveAddSubtype(): void {
    const name = this.addSubtypeForm.name?.trim();
    if (!name) return;
    this.errorMessage = null;
    this.saving = true;
    this.cdr.markForCheck();
    const payload = {
      subtype: {
        name,
        visibility: this.addSubtypeForm.visibility?.trim() || 'private',
      },
    };
    this.http
      .post<{ subtype_id?: number }>(
        `${environment.apiUrl}/events/subtypes`,
        payload,
        { withCredentials: true }
      )
      .subscribe({
        next: (res) => {
          const subtypeId = res?.subtype_id ?? undefined;
          this.showAddSubtype = false;
          this.saving = false;
          this.pendingSubtypeId = subtypeId ?? null;
          this.loadSubtypes();
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.errorMessage =
            err?.error?.message ||
            err?.message ||
            'Kunne ikke oprette indholdstema';
          this.saving = false;
          this.cdr.markForCheck();
        },
      });
  }

  loadModuleVersions(): void {
    if (!this.selectedModule?.module_id) return;
    this.loadingModuleVersions = true;
    this.cdr.markForCheck();
    this.http
      .get<ModuleVersionItem[] | { items: ModuleVersionItem[] }>(
        `${environment.apiUrl}/events/modules/${this.selectedModule.module_id}/versions`,
        { withCredentials: true }
      )
      .pipe(catchError(() => of([])))
      .subscribe((res) => {
        const list = Array.isArray(res) ? res : res?.items || [];
        this.moduleVersions = list
          .map((v) => ({
            ...v,
            dateRange: this.formatDateRange(v.valid_from, v.valid_to),
            description: this.getDescriptionPreview(v.descriptions),
          }))
          .sort(
            (a, b) =>
              new Date(b.valid_from).getTime() -
              new Date(a.valid_from).getTime()
          );
        this.loadingModuleVersions = false;
        this.cdr.markForCheck();
      });
  }

  loadSubtypeVersions(): void {
    if (!this.selectedSubtype?.subtype_id) return;
    this.loadingSubtypeVersions = true;
    this.cdr.markForCheck();
    this.http
      .get<SubtypeVersionItem[] | { items: SubtypeVersionItem[] }>(
        `${environment.apiUrl}/events/subtypes/${this.selectedSubtype.subtype_id}/versions`,
        { withCredentials: true }
      )
      .pipe(catchError(() => of([])))
      .subscribe((res) => {
        const list = Array.isArray(res) ? res : res?.items || [];
        this.subtypeVersions = list
          .map((v) => ({
            ...v,
            dateRange: this.formatDateRange(v.valid_from, v.valid_to),
            description: this.getDescriptionPreview(v.descriptions),
          }))
          .sort(
            (a, b) =>
              new Date(b.valid_from).getTime() -
              new Date(a.valid_from).getTime()
          );
        this.loadingSubtypeVersions = false;
        this.cdr.markForCheck();
      });
  }

  formatDateRange(from: string, to: string | null): string {
    if (!from) return '';
    const fromStr = new Date(from).toLocaleDateString('da-DK');
    const toStr = to ? new Date(to).toLocaleDateString('da-DK') : 'Pågående';
    return `${fromStr} – ${toStr}`;
  }

  private readonly langLabels: Record<string, string> = {
    da: 'Dansk',
    en: 'Engelsk',
    de: 'Tysk',
  };

  getDescriptionPreview(
    descriptions: Record<string, { translation_text?: string }> | undefined
  ): string {
    if (!descriptions || typeof descriptions !== 'object') return '';
    const da = descriptions['da'];
    const text = da?.translation_text ?? (typeof da === 'string' ? da : '');
    return text && text.length > 80
      ? text.substring(0, 77) + '...'
      : text || '';
  }

  /** Returns description lines for display (all languages), visible without editing. */
  getDescriptionLines(
    descriptions: Record<string, { translation_text?: string }> | undefined
  ): Array<{ lang: string; label: string; text: string }> {
    if (!descriptions || typeof descriptions !== 'object') {
      return this.emptyDescriptionLines;
    }
    const cached = this.descriptionLinesCache.get(descriptions);
    if (cached) {
      return cached;
    }
    const order = ['da', 'en', 'de'];
    const lines = order
      .filter((lang) => descriptions[lang]?.translation_text)
      .map((lang) => ({
        lang,
        label: this.langLabels[lang] || lang,
        text: descriptions[lang]?.translation_text ?? '',
      }));
    this.descriptionLinesCache.set(descriptions, lines);
    return lines;
  }

  openAddModuleVersion(): void {
    const year = new Date().getFullYear();
    this.addModuleVersionForm = {
      valid_from: `${year}-01-01`,
      valid_to: `${year}-12-31`,
      maintenance_minutes: 0,
      description_da: '',
      description_en: '',
      description_de: '',
    };
    this.showAddModuleVersion = true;
    this.errorMessage = null;
    this.cdr.markForCheck();
  }

  cancelAddModuleVersion(): void {
    this.showAddModuleVersion = false;
    this.errorMessage = null;
    this.cdr.markForCheck();
  }

  saveAddModuleVersion(): void {
    if (
      !this.selectedModule?.module_id ||
      !this.addModuleVersionForm.valid_from
    )
      return;
    this.errorMessage = null;
    this.saving = true;
    this.cdr.markForCheck();
    const dest = this.dataDestService.getDataDest('add_module_version');
    if (!dest) {
      this.errorMessage = 'add_module_version dest not registered';
      this.saving = false;
      this.cdr.markForCheck();
      return;
    }
    const descs: Record<
      string,
      { language_code: string; translation_text: string }
    > = {};
    if (this.addModuleVersionForm.description_da?.trim())
      descs['da'] = {
        language_code: 'da',
        translation_text: this.addModuleVersionForm.description_da.trim(),
      };
    if (this.addModuleVersionForm.description_en?.trim())
      descs['en'] = {
        language_code: 'en',
        translation_text: this.addModuleVersionForm.description_en.trim(),
      };
    if (this.addModuleVersionForm.description_de?.trim())
      descs['de'] = {
        language_code: 'de',
        translation_text: this.addModuleVersionForm.description_de.trim(),
      };
    const payload = {
      module_id: this.selectedModule.module_id,
      valid_from: this.addModuleVersionForm.valid_from,
      valid_to: this.addModuleVersionForm.valid_to || null,
      maintenance_minutes: this.addModuleVersionForm.maintenance_minutes || 0,
      descriptions: Object.keys(descs).length ? descs : undefined,
    };
    (dest as (data: any) => any)(payload).subscribe({
      next: () => {
        this.showAddModuleVersion = false;
        this.loadModuleVersions();
        this.saving = false;
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.errorMessage =
          err?.error?.message || err?.message || 'Kunne ikke oprette version';
        this.saving = false;
        this.cdr.markForCheck();
      },
    });
  }

  openEditModuleVersion(v: ModuleVersionItem): void {
    this.editingModuleVersionId = v.version_id;
    this.editModuleVersionForm = {
      valid_from: v.valid_from || '',
      valid_to: v.valid_to || '',
      maintenance_minutes: v.maintenance_minutes ?? 0,
      description_da: v.descriptions?.['da']?.translation_text ?? '',
      description_en: v.descriptions?.['en']?.translation_text ?? '',
      description_de: v.descriptions?.['de']?.translation_text ?? '',
    };
    this.cdr.markForCheck();
  }

  cancelEditModuleVersion(): void {
    this.editingModuleVersionId = null;
    this.cdr.markForCheck();
  }

  saveEditModuleVersion(): void {
    if (this.editingModuleVersionId == null || !this.selectedModule) return;
    this.saving = true;
    this.errorMessage = null;
    this.cdr.markForCheck();
    const dest = this.dataDestService.getDataDest('update_module_version');
    if (!dest) {
      this.saving = false;
      this.cdr.markForCheck();
      return;
    }
    const editingVersion = this.moduleVersions.find(
      (v) => v.version_id === this.editingModuleVersionId
    );
    const editDescs: Record<
      string,
      {
        language_code: string;
        translation_text: string;
        description_id?: number;
      }
    > = {};
    const langs = ['da', 'en', 'de'] as const;
    for (const lang of langs) {
      const text =
        lang === 'da'
          ? this.editModuleVersionForm.description_da
          : lang === 'en'
          ? this.editModuleVersionForm.description_en
          : this.editModuleVersionForm.description_de;
      const trimmed = text?.trim() ?? '';
      const existing = editingVersion?.descriptions?.[lang];
      editDescs[lang] = {
        language_code: lang,
        translation_text: trimmed,
        ...(existing?.description_id != null && {
          description_id: existing.description_id,
        }),
      };
    }
    (dest as (data: any) => any)({
      version_id: this.editingModuleVersionId,
      module_id: this.selectedModule.module_id,
      valid_from: this.editModuleVersionForm.valid_from,
      valid_to: this.editModuleVersionForm.valid_to || null,
      maintenance_minutes: this.editModuleVersionForm.maintenance_minutes,
      descriptions: editDescs,
    }).subscribe({
      next: () => {
        this.editingModuleVersionId = null;
        this.loadModuleVersions();
        this.saving = false;
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.errorMessage =
          err?.error?.message || err?.message || 'Kunne ikke opdatere';
        this.saving = false;
        this.cdr.markForCheck();
      },
    });
  }

  deleteModuleVersion(v: ModuleVersionItem): void {
    if (!confirm('Er du sikker på, at du vil slette denne version?')) return;
    const dest = this.dataDestService.getDataDest('delete_module_version');
    if (!dest || !this.selectedModule) return;
    (dest as (data: any) => any)({
      version_id: v.version_id,
      module_id: this.selectedModule.module_id,
    }).subscribe({
      next: () => this.loadModuleVersions(),
      error: (err: any) => {
        this.errorMessage =
          err?.error?.message || err?.message || 'Kunne ikke slette';
        this.cdr.markForCheck();
      },
    });
  }

  openAddSubtypeVersion(): void {
    const year = new Date().getFullYear();
    this.addSubtypeVersionForm = {
      valid_from: `${year}-01-01`,
      valid_to: `${year}-12-31`,
      description_da: '',
    };
    this.showAddSubtypeVersion = true;
    this.errorMessage = null;
    this.cdr.markForCheck();
  }

  cancelAddSubtypeVersion(): void {
    this.showAddSubtypeVersion = false;
    this.errorMessage = null;
    this.cdr.markForCheck();
  }

  saveAddSubtypeVersion(): void {
    if (
      !this.selectedSubtype?.subtype_id ||
      !this.addSubtypeVersionForm.valid_from
    )
      return;
    this.errorMessage = null;
    this.saving = true;
    this.cdr.markForCheck();
    const dest = this.dataDestService.getDataDest('add_subtype_version');
    if (!dest) {
      this.errorMessage = 'add_subtype_version dest not registered';
      this.saving = false;
      this.cdr.markForCheck();
      return;
    }
    (dest as (data: any) => any)({
      subtype_id: this.selectedSubtype.subtype_id,
      valid_from: this.addSubtypeVersionForm.valid_from,
      valid_to: this.addSubtypeVersionForm.valid_to || null,
      descriptions: this.addSubtypeVersionForm.description_da
        ? {
            da: {
              language_code: 'da',
              translation_text: this.addSubtypeVersionForm.description_da,
            },
          }
        : undefined,
    }).subscribe({
      next: () => {
        this.showAddSubtypeVersion = false;
        this.loadSubtypeVersions();
        this.saving = false;
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.errorMessage =
          err?.error?.message || err?.message || 'Kunne ikke oprette version';
        this.saving = false;
        this.cdr.markForCheck();
      },
    });
  }

  openEditSubtypeVersion(v: SubtypeVersionItem): void {
    this.editingSubtypeVersionId = v.version_id;
    this.editSubtypeVersionForm = {
      valid_from: v.valid_from || '',
      valid_to: v.valid_to || '',
      description_da: v.descriptions?.['da']?.translation_text ?? '',
    };
    this.cdr.markForCheck();
  }

  cancelEditSubtypeVersion(): void {
    this.editingSubtypeVersionId = null;
    this.cdr.markForCheck();
  }

  saveEditSubtypeVersion(): void {
    if (this.editingSubtypeVersionId == null || !this.selectedSubtype) return;
    this.saving = true;
    this.errorMessage = null;
    this.cdr.markForCheck();
    const dest = this.dataDestService.getDataDest('update_subtype_version');
    if (!dest) {
      this.saving = false;
      this.cdr.markForCheck();
      return;
    }
    (dest as (data: any) => any)({
      version_id: this.editingSubtypeVersionId,
      subtype_id: this.selectedSubtype.subtype_id,
      valid_from: this.editSubtypeVersionForm.valid_from,
      valid_to: this.editSubtypeVersionForm.valid_to || null,
      descriptions: this.editSubtypeVersionForm.description_da
        ? {
            da: {
              language_code: 'da',
              translation_text: this.editSubtypeVersionForm.description_da,
            },
          }
        : undefined,
    }).subscribe({
      next: () => {
        this.editingSubtypeVersionId = null;
        this.loadSubtypeVersions();
        this.saving = false;
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.errorMessage =
          err?.error?.message || err?.message || 'Kunne ikke opdatere';
        this.saving = false;
        this.cdr.markForCheck();
      },
    });
  }

  deleteSubtypeVersion(v: SubtypeVersionItem): void {
    if (!confirm('Er du sikker på, at du vil slette denne version?')) return;
    const dest = this.dataDestService.getDataDest('delete_subtype_version');
    if (!dest || !this.selectedSubtype) return;
    (dest as (data: any) => any)({
      version_id: v.version_id,
      subtype_id: this.selectedSubtype.subtype_id,
    }).subscribe({
      next: () => this.loadSubtypeVersions(),
      error: (err: any) => {
        this.errorMessage =
          err?.error?.message || err?.message || 'Kunne ikke slette';
        this.cdr.markForCheck();
      },
    });
  }

}
